import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo, useEffect } from "react";
import {
	Line,
	LineChart,
	XAxis,
	YAxis,
	ResponsiveContainer,
	Tooltip,
	CartesianGrid,
} from "recharts";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { formatDate } from "@/lib/utils/date";
import {
	AlertTriangle,
	FileDown,
	BarChart,
	FileText,
	FileType,
	FileSpreadsheet,
	FileCode,
	Loader2,
	Brain,
	Sparkles,
	Zap,
	Target,
	Image,
	Smartphone,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuTrigger,
	DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { ChartExportButton } from "@/components/ui/chart-export-button";
import { ExportAllChartsButton } from "@/components/ui/export-all-charts-button";
import {
	useGetDevicesQuery,
	useGetLocationsQuery,
	useGetAnalyticsSensorAggregatesQuery,
	useGetAnalyticsDeviceStatusQuery,
	useLazyGetExportSensorAggregatesQuery,
	type GetAnalyticsSensorAggregatesApiArg,
} from "@/services";
import { format, subDays } from "date-fns";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GaugeChart } from "@/components/analytics/GaugeChart";
import { DeviceStatusTable } from "@/components/analytics/DeviceStatusTable";
import { toast } from "@/components/ui/toaster";
import { aiService } from "@/services/ai-service";
import {
	AIPredictionWidget,
	AIMetricsWidget,
} from "@/components/ai/ai-dashboard-widgets";
import { Badge } from "@/components/ui/badge";
import { AnalyticsFilters } from "@/components/analytics/filters/analytics-filters";
import { StatusCards } from "@/components/analytics/status-cards";
import { DashboardHeader } from "@/components/analytics/dashboard-header";

// Theme-compliant color palette
const THEME_COLORS = {
	chart1: "hsl(var(--chart-1))", // Primary teal
	chart2: "hsl(var(--chart-2))", // Blue
	chart3: "hsl(var(--chart-3))", // Indigo
	chart6: "hsl(var(--chart-6))", // Red/Orange
	chart7: "hsl(var(--chart-7))", // Amber
	chart8: "hsl(var(--chart-8))", // Green
	success: "hsl(var(--success))",
	warning: "hsl(var(--warning))",
	destructive: "hsl(var(--destructive))",
};

// Types
interface Location {
	id: string;
	name: string;
	subLocations?: Location[];
}

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label, unit }: any) => {
	if (active && payload && payload.length) {
		return (
			<div className="bg-background/95 border rounded-lg shadow-lg p-3 backdrop-blur-sm">
				<p className="text-sm font-medium">{formatDate.fullDateTime(label)}</p>
				{payload.map((entry: any, index: number) => (
					<div key={index} className="flex items-center gap-2 mt-1">
						<div
							className="w-3 h-3 rounded-full"
							style={{ backgroundColor: entry.color }}
						/>
						<span className="text-sm">
							{entry.name}: {entry.value.toFixed(1)} {unit || entry.unit || ""}
						</span>
					</div>
				))}
			</div>
		);
	}
	return null;
};

// Format date based on interval
const formatDateByInterval = (timestamp: number, interval: string): string => {
	const date = new Date(timestamp);
	switch (interval) {
		case "minute":
			return format(date, "HH:mm");
		case "hour":
			return format(date, "ha");
		case "day":
			return format(date, "d MMM");
		case "week":
			return format(date, "d MMM");
		case "month":
			return format(date, "MMM");
		default:
			return format(date, "HH:mm");
	}
};

// Visualization component for aggregated sensor data
function SensorAggregateChart({
	data,
	title,
	color,
	unit,
	dataKey = "avg",
	secondaryDataKey,
	secondaryColor,
	domainMin,
	domainMax,
	interval,
	id,
	deviceInfo,
	locationInfo,
}: {
	data: any[];
	title: string;
	color: string;
	unit: string;
	dataKey?: string;
	secondaryDataKey?: string;
	secondaryColor?: string;
	domainMin?: number;
	domainMax?: number;
	interval: string;
	id: string;
	deviceInfo?: string;
	locationInfo?: string;
}) {
	if (!data || data.length === 0) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>{title}</CardTitle>
					<CardDescription>No data available</CardDescription>
				</CardHeader>
				<CardContent className="h-[300px] flex items-center justify-center text-muted-foreground">
					No sensor data found for the selected period
				</CardContent>
			</Card>
		);
	}

	const formattedData = data.map((item) => ({
		...item,
		timestamp: new Date(item.timestamp).getTime(),
	}));

	// Calculate min/max for Y axis domain
	const values = formattedData.map((item) => item[dataKey]);
	const minValue =
		domainMin !== undefined ? domainMin : Math.floor(Math.min(...values) * 0.9);
	const maxValue =
		domainMax !== undefined ? domainMax : Math.ceil(Math.max(...values) * 1.1);

	// Calculate current value (most recent data point)
	const latestValue = formattedData[formattedData.length - 1]?.[dataKey] || 0;

	// Get date range for metadata
	const startDate =
		formattedData.length > 0
			? format(new Date(formattedData[0]?.timestamp), "PPP")
			: "";
	const endDate =
		formattedData.length > 0
			? format(
					new Date(formattedData[formattedData.length - 1]?.timestamp),
					"PPP",
				)
			: "";
	const dateRange = `${startDate} - ${endDate}`;

	// Calculate useful statistics
	const avgValue =
		formattedData.reduce((sum, item) => sum + item[dataKey], 0) /
		formattedData.length;
	const stdDev = Math.sqrt(
		formattedData.reduce(
			(sum, item) => sum + Math.pow(item[dataKey] - avgValue, 2),
			0,
		) / formattedData.length,
	);
	const medianValue = [...values].sort((a, b) => a - b)[
		Math.floor(values.length / 2)
	];

	return (
		<Card>
			<CardHeader className="flex flex-row items-center justify-between pb-2">
				<div>
					<CardTitle className="flex items-center justify-between">
						<span>{title}</span>
						<span className="text-lg font-semibold">
							{latestValue.toFixed(1)} {unit}
						</span>
					</CardTitle>
					<CardDescription data-date-range={dateRange}>
						{startDate} - {endDate}
					</CardDescription>
				</div>
				<ChartExportButton
					chartId={id}
					filename={`${title.toLowerCase().replace(/\s+/g, "-")}`}
					metadata={{
						title: title,
						description: `${title} data over time (${interval} intervals)`,
						dateRange: dateRange,
						additionalInfo: {
							"Sensor Type": title,
							Unit: unit,
							Device: deviceInfo || "All Devices",
							Location: locationInfo || "All Locations",
							"Time Interval": interval,
							Average: `${avgValue.toFixed(2)} ${unit}`,
							Median: `${medianValue.toFixed(2)} ${unit}`,
							"Standard Deviation": `${stdDev.toFixed(2)}`,
							Min: `${minValue.toFixed(2)} ${unit}`,
							Max: `${maxValue.toFixed(2)} ${unit}`,
							Latest: `${latestValue.toFixed(2)} ${unit}`,
							"Data Points": `${formattedData.length}`,
							"Start Date": startDate,
							"End Date": endDate,
						},
					}}
				/>
			</CardHeader>
			<CardContent>
				<div className="h-[300px]" id={id}>
					<ResponsiveContainer width="100%" height="100%">
						<LineChart
							data={formattedData}
							margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
						>
							<CartesianGrid
								strokeDasharray="3 3"
								vertical={false}
								opacity={0.2}
							/>
							<XAxis
								dataKey="timestamp"
								tickFormatter={(time) => formatDateByInterval(time, interval)}
								stroke="hsl(var(--muted-foreground))"
								fontSize={12}
								type="number"
								domain={["auto", "auto"]}
								scale="time"
							/>
							<YAxis
								domain={[minValue, maxValue]}
								stroke="hsl(var(--muted-foreground))"
								fontSize={12}
								tickFormatter={(value) => `${value.toFixed(1)}`}
							/>
							<Tooltip
								content={<CustomTooltip unit={unit} />}
								labelFormatter={(label) =>
									formatDate.fullDateTime(new Date(label))
								}
							/>
							<Line
								type="monotone"
								dataKey={dataKey}
								name={title}
								stroke={color}
								strokeWidth={2}
								dot={false}
								activeDot={{ r: 6, strokeWidth: 0 }}
								isAnimationActive={true}
								animationDuration={300}
							/>
							{secondaryDataKey && secondaryColor && (
								<Line
									type="monotone"
									dataKey={secondaryDataKey}
									name={`${title} (${secondaryDataKey})`}
									stroke={secondaryColor}
									strokeWidth={1.5}
									strokeDasharray="5 5"
									dot={false}
									isAnimationActive={true}
									animationDuration={300}
								/>
							)}
						</LineChart>
					</ResponsiveContainer>
				</div>
				<div className="mt-3 text-xs text-muted-foreground flex justify-between">
					<span>
						Min: {minValue.toFixed(1)} {unit}
					</span>
					<span>
						Avg: {avgValue.toFixed(1)} {unit}
					</span>
					<span>
						Max: {maxValue.toFixed(1)} {unit}
					</span>
				</div>
			</CardContent>
		</Card>
	);
}

export const Route = createFileRoute(
	"/_authenticated/(user-dashboard)/dashboard/_dashboard/analytics",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const [liveData, setLiveData] = useState(false);
	const [isRefreshing, setIsRefreshing] = useState(false);
	// State for filters and tabs
	const [selectedLocation, setSelectedLocation] = useState<string>("");
	const [selectedDevice, setSelectedDevice] = useState<string>("");
	const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>({
		from: subDays(new Date(), 7),
		to: new Date(),
	});
	const [interval, setInterval] =
		useState<Required<GetAnalyticsSensorAggregatesApiArg>["interval"]>("hour");
	const [activeTab, setActiveTab] = useState<string>("timeseries");
	const [aiInsights, setAiInsights] = useState<any[]>([]);
	const [aiLoading, setAiLoading] = useState(false);

	// Add queries for locations and devices
	const { data: locationsData } = useGetLocationsQuery({});

	const { data: devicesData } = useGetDevicesQuery({
		locationId: selectedLocation || undefined,
	});

	// Fetch aggregated sensor data
	const {
		data: sensorAggregates,
		isLoading: isLoadingAggregates,
		error: aggregatesError,
	} = useGetAnalyticsSensorAggregatesQuery({
		locationId: selectedLocation || undefined,
		deviceId: selectedDevice || undefined,
		startDate: dateRange.from.toISOString(),
		endDate: dateRange.to.toISOString(),
		interval,
	});

	// Fetch device status data
	const { data: deviceStatus, isLoading: isLoadingStatus } =
		useGetAnalyticsDeviceStatusQuery({
			locationId: selectedLocation || undefined,
			limit: "100",
		});

	// Format locations data for the dropdown
	const locations = useMemo(() => {
		if (!locationsData?.data) return [];

		const formatLocations = (locations: any[]): Location[] => {
			return locations.map((loc) => ({
				id: loc.id,
				name: loc.name,
				subLocations: loc.children ? formatLocations(loc.children) : undefined,
			}));
		};

		return formatLocations(locationsData.data);
	}, [locationsData]);

	// Process aggregated data by sensor type
	const aggregatedData = useMemo(() => {
		if (!sensorAggregates?.data) return {};

		const result: Record<string, any[]> = {};

		sensorAggregates.data.forEach((item) => {
			if (!result[item.sensor_type]) {
				result[item.sensor_type] = [];
			}
			result[item.sensor_type].push(item);
		});

		// Sort each group by timestamp
		Object.keys(result).forEach((key) => {
			result[key].sort(
				(a, b) =>
					new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
			);
		});

		return result;
	}, [sensorAggregates]);

	// Handle device selection
	const handleDeviceChange = (deviceId: string) => {
		setSelectedDevice(deviceId);
	};

	// Handle live toggle
	const handleLiveToggle = (checked: boolean) => {
		setLiveData(checked);
		if (checked) {
			toast.success("Coming Soon", {
				title: "Coming Soon",
				description:
					"Real-time data streaming will be available in a future update.",
				duration: 3000,
			});
			// Reset after showing toast
			setTimeout(() => setLiveData(false), 500);
		}
	};

	// Handle refresh
	const handleRefresh = async () => {
		setIsRefreshing(true);
		try {
			// Force refetch of all data
			await Promise.all([
				// Refetch sensor aggregates
				// Refetch device status
				// Any other data that needs refreshing
			]);

			toast.success("Data refreshed", {
				title: "Success",
				description: "Analytics data has been refreshed successfully.",
				duration: 2000,
			});
		} catch (_error) {
			toast.error("Refresh failed", {
				title: "Error",
				description: "Failed to refresh data. Please try again.",
				duration: 3000,
			});
		} finally {
			setIsRefreshing(false);
		}
	};

	// Generate AI insights from analytics data
	useEffect(() => {
		const generateAiInsights = async () => {
			if (!sensorAggregates?.data || sensorAggregates.data.length === 0) return;

			setAiLoading(true);
			try {
				const formattedData = sensorAggregates.data.map((item) => ({
					timestamp: item.timestamp,
					value: item.avg,
					sensor_type: item.sensor_type,
					device_id: selectedDevice || "all",
					unit: getSensorUnit(item.sensor_type),
				}));

				const insights = await aiService.generateAdvancedInsights(
					devicesData?.data || [],
					formattedData,
					`${Math.abs(dateRange.to.getTime() - dateRange.from.getTime()) / (1000 * 60 * 60)}h`,
				);

				setAiInsights(insights.slice(0, 5)); // Show top 5 insights
			} catch (error) {
				console.error("Failed to generate AI insights:", error);
			} finally {
				setAiLoading(false);
			}
		};

		if (sensorAggregates?.data) {
			generateAiInsights();
		}
	}, [
		sensorAggregates,
		selectedDevice,
		devicesData,
		dateRange.from,
		dateRange.to,
	]);

	// Determine unit based on sensor type
	const getSensorUnit = (sensorType: string): string => {
		switch (sensorType.toLowerCase()) {
			case "temperature":
				return "°C";
			case "humidity":
				return "%";
			case "co2":
				return "ppm";
			case "pressure":
				return "hPa";
			case "light":
				return "lux";
			default:
				return "";
		}
	};

	// Get chart color by sensor type - more explicit naming of RGB values for clarity
	const getSensorColor = (sensorType: string, opacity: number = 1): string => {
		// Define basic RGB colors for each sensor type
		const colors: Record<string, [number, number, number]> = {
			temperature: [255, 59, 48], // Red
			humidity: [0, 122, 255], // Blue
			co2: [50, 205, 50], // Green
			pressure: [175, 82, 222], // Purple
			light: [255, 204, 0], // Yellow
			default: [52, 152, 219], // Default blue
		};

		// Get the RGB values for the requested sensor type
		const [r, g, b] = colors[sensorType.toLowerCase()] || colors.default;

		// Return color with specified opacity
		return `rgba(${r}, ${g}, ${b}, ${opacity})`;
	};

	// Get sensor display name
	const getSensorDisplayName = (sensorType: string): string => {
		switch (sensorType.toLowerCase()) {
			case "temperature":
				return "Temperature";
			case "humidity":
				return "Humidity";
			case "co2":
				return "CO₂ Level";
			case "pressure":
				return "Air Pressure";
			case "light":
				return "Light Level";
			default:
				return sensorType;
		}
	};

	const getChartType = (
		activeChart: string,
	): "line" | "area" | "gauge" | undefined => {
		switch (activeChart.toLowerCase()) {
			case "timeseries":
				return "line";
			case "gauges":
				return "gauge";
			default:
				return undefined;
		}
	};

	const [triggerExport] = useLazyGetExportSensorAggregatesQuery();
	const [isExporting, setIsExporting] = useState(false);

	const handleExport = async (
		exportFormat: "json" | "csv" | "pdf" | "xlsx" | "chart",
	) => {
		const chartType = getChartType(activeTab);
		const startDateStr = dateRange.from.toISOString().replace(/:/g, "-");
		const endDateStr = dateRange.to.toISOString().replace(/:/g, "-");
		const fileExtension = exportFormat === "chart" ? "pdf" : exportFormat;
		const filename = `sensor-aggregates-${startDateStr}TO${endDateStr}.${fileExtension}`;

		try {
			setIsExporting(true);

			const result = await triggerExport({
				locationId: selectedLocation || undefined,
				deviceId: selectedDevice || undefined,
				startDate: dateRange.from.toISOString(),
				endDate: dateRange.to.toISOString(),
				interval,
				format: exportFormat,
				chartType: exportFormat === "chart" ? chartType : undefined,
			}).unwrap();

			// RTK Query with responseHandler: 'blob' returns a Blob directly
			// But we need to ensure it's a valid Blob instance
			let blob: Blob;
			if (result instanceof Blob) {
				blob = result;
			} else {
				// Fallback: if result is not a Blob, try to create one from it
				const mimeTypes: Record<string, string> = {
					csv: "text/csv",
					json: "application/json",
					xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
					pdf: "application/pdf",
					chart: "application/pdf",
				};
				blob = new Blob([result as BlobPart], {
					type: mimeTypes[exportFormat],
				});
			}

			const downloadUrl = URL.createObjectURL(blob);

			const link = document.createElement("a");
			link.href = downloadUrl;
			link.download = filename;
			document.body.appendChild(link);
			link.click();
			link.remove();

			URL.revokeObjectURL(downloadUrl);
			toast.success("Export completed successfully.");
		} catch (err) {
			console.error("Export error:", err);
			toast.error("Error exporting data. Please try again.");
		} finally {
			setIsExporting(false);
		}
	};

	return (
		<div className="space-y-6">
			{/* Dashboard Header */}
			<DashboardHeader
				title="Analytics Dashboard"
				description="Monitor sensor data, device performance, and gain AI-powered insights"
			/>

			{/* Analytics Filters */}
			<AnalyticsFilters
				locations={locations}
				selectedLocation={selectedLocation}
				onLocationChange={setSelectedLocation}
				devices={devicesData?.data || []}
				selectedDevice={selectedDevice}
				onDeviceChange={handleDeviceChange}
				dateRange={dateRange}
				onDateRangeChange={setDateRange}
				interval={interval}
				onIntervalChange={(val) => setInterval(val as typeof interval)}
				liveData={liveData}
				onLiveDataToggle={handleLiveToggle}
				isRefreshing={isRefreshing}
				onRefresh={handleRefresh}
			/>

			{/* Status Cards */}
			{!isLoadingAggregates && !aggregatesError && (
				<StatusCards
					activeSensors={Object.keys(aggregatedData).length}
					connectedDevices={devicesData?.data?.length || 0}
					dataPoints={Object.values(aggregatedData).reduce(
						(sum, arr) => sum + arr.length,
						0,
					)}
					isLiveData={liveData}
					isLoading={isLoadingAggregates}
				/>
			)}

			{/* Tabs and Export Controls */}
			<Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
				<div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
					<TabsList className="grid w-full max-w-md grid-cols-4">
						<TabsTrigger value="timeseries" className="text-xs">
							<BarChart className="h-4 w-4 mr-1.5" />
							Time Series
						</TabsTrigger>
						<TabsTrigger value="gauges" className="text-xs">
							<Target className="h-4 w-4 mr-1.5" />
							Gauges
						</TabsTrigger>
						<TabsTrigger value="devices" className="text-xs">
							<Smartphone className="h-4 w-4 mr-1.5" />
							Devices
						</TabsTrigger>
						<TabsTrigger value="ai-insights" className="text-xs">
							<Brain className="h-4 w-4 mr-1.5" />
							AI Insights
						</TabsTrigger>
					</TabsList>

					<div className="flex items-center gap-2">
						{activeTab !== "devices" && (
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button
										variant="outline"
										size="sm"
										disabled={isExporting}
										className="gap-2"
									>
										{isExporting ? (
											<>
												<Loader2 className="h-4 w-4 animate-spin" />
												Exporting...
											</>
										) : (
											<>
												<FileDown className="h-4 w-4" />
												Export Data
											</>
										)}
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent className="w-[160px]" align="end">
									{/* <DropdownMenuItem
										onClick={() => handleExport("pdf")}
										disabled={isExporting}
									>
										<FileType className="mr-2 h-4 w-4" />
										Export as PDF
									</DropdownMenuItem> */}
									<DropdownMenuItem
										onClick={() => handleExport("csv")}
										disabled={isExporting}
									>
										<FileText className="mr-2 h-4 w-4" />
										Export as CSV
									</DropdownMenuItem>
									<DropdownMenuItem
										onClick={() => handleExport("xlsx")}
										disabled={isExporting}
									>
										<FileSpreadsheet className="mr-2 h-4 w-4" />
										Export as Excel
									</DropdownMenuItem>
									<DropdownMenuItem
										onClick={() => handleExport("json")}
										disabled={isExporting}
									>
										<FileCode className="mr-2 h-4 w-4" />
										Export as JSON
									</DropdownMenuItem>
									{/* <DropdownMenuItem
										onClick={() => handleExport("chart")}
										disabled={isExporting}
									>
										<Image className="mr-2 h-4 w-4" />
										Export as Image
									</DropdownMenuItem> */}
								</DropdownMenuContent>
							</DropdownMenu>
						)}

						{activeTab === "timeseries" &&
							Object.keys(aggregatedData).length > 0 && (
								<ExportAllChartsButton
									containerId="timeseries-charts-container"
									filename="iot-time-series-charts"
									label="Export All"
									size="sm"
									variant="outline"
									metadata={{
										title: "IoT Sensor Data Analysis",
										description:
											"Time series visualization of sensor readings across all devices",
										dateRange: `${format(dateRange.from, "PPP")} to ${format(dateRange.to, "PPP")}`,
										additionalInfo: {
											"Data Interval": interval,
											Device: selectedDevice
												? devicesData?.data?.find(
														(d) => d.id === selectedDevice,
													)?.name || selectedDevice
												: "All Devices",
											Location: selectedLocation
												? locations.find((l) => l.id === selectedLocation)
														?.name || selectedLocation
												: "All Locations",
											"Number of Sensors":
												Object.keys(aggregatedData).length.toString(),
											"Sensor Types": Object.keys(aggregatedData)
												.map(getSensorDisplayName)
												.join(", "),
											"Data Period": `${format(dateRange.from, "PPP HH:mm")} to ${format(dateRange.to, "PPP HH:mm")}`,
											"Data Points": Object.values(aggregatedData)
												.reduce((sum, arr) => sum + arr.length, 0)
												.toString(),
											"Report Generated": format(new Date(), "PPP HH:mm:ss"),
										},
									}}
								/>
							)}

						{activeTab === "gauges" &&
							Object.keys(aggregatedData).length > 0 && (
								<ExportAllChartsButton
									containerId="gauge-charts-container"
									filename="iot-gauge-charts"
									label="Export All"
									size="sm"
									variant="outline"
									metadata={{
										title: "IoT Sensor Data Analysis - Gauges",
										description:
											"Gauge chart representation of sensor readings across all devices",
										dateRange: `${format(dateRange.from, "PPP")} to ${format(dateRange.to, "PPP")}`,
										additionalInfo: {
											"Data Interval": interval,
											Device: selectedDevice
												? devicesData?.data?.find(
														(d) => d.id === selectedDevice,
													)?.name || selectedDevice
												: "All Devices",
											Location: selectedLocation
												? locations.find((l) => l.id === selectedLocation)
														?.name || selectedLocation
												: "All Locations",
											"Number of Sensors":
												Object.keys(aggregatedData).length.toString(),
											"Sensor Types": Object.keys(aggregatedData)
												.map(getSensorDisplayName)
												.join(", "),
											"Data Period": `${format(dateRange.from, "PPP HH:mm")} to ${format(dateRange.to, "PPP HH:mm")}`,
											"Current Values": Object.entries(aggregatedData)
												.map(([sensorType, data]) => {
													const sortedData = [...data].sort(
														(a, b) =>
															new Date(b.timestamp).getTime() -
															new Date(a.timestamp).getTime(),
													);
													const latestValue = sortedData[0]?.avg || 0;
													return `${getSensorDisplayName(sensorType)}: ${latestValue.toFixed(1)} ${getSensorUnit(sensorType)}`;
												})
												.join(", "),
											"Report Generated": format(new Date(), "PPP HH:mm:ss"),
										},
									}}
								/>
							)}
					</div>
				</div>

				<AnimatePresence>
					{aggregatesError && (
						<motion.div
							initial={{ opacity: 0, y: -10 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -10 }}
							className="rounded-md bg-destructive/10 p-4 mt-4"
						>
							<div className="flex">
								<div className="text-destructive">
									<AlertTriangle className="h-5 w-5" />
								</div>
								<div className="ml-3">
									<h3 className="text-sm font-medium text-destructive">
										Error loading sensor data
									</h3>
								</div>
							</div>
						</motion.div>
					)}

					{isLoadingAggregates || isLoadingStatus ? (
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							className="flex items-center justify-center h-[200px] mt-4"
						>
							<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
						</motion.div>
					) : (
						<>
							<TabsContent value="timeseries" className="space-y-4 mt-4">
								<div
									id="timeseries-charts-container"
									className="grid grid-cols-1 md:grid-cols-2 gap-4"
								>
									{Object.keys(aggregatedData).length === 0 ? (
										<div className="col-span-full py-16 text-center">
											<BarChart className="mx-auto h-12 w-12 text-muted-foreground/50" />
											<h3 className="mt-4 text-lg font-semibold">
												No Data Available
											</h3>
											<p className="mt-2 text-muted-foreground">
												Try adjusting your filters or selecting different
												devices.
											</p>
										</div>
									) : (
										<>
											{Object.entries(aggregatedData).map(
												([sensorType, data]) => (
													<SensorAggregateChart
														key={sensorType}
														id={`chart-${sensorType}`}
														data={data}
														title={getSensorDisplayName(sensorType)}
														color={getSensorColor(sensorType)}
														unit={getSensorUnit(sensorType)}
														dataKey="avg"
														secondaryDataKey={
															sensorType === "temperature" ||
															sensorType === "humidity"
																? "max"
																: undefined
														}
														secondaryColor={
															sensorType === "temperature" ||
															sensorType === "humidity"
																? getSensorColor(sensorType, 0.5)
																: undefined
														}
														interval={interval}
														deviceInfo={
															selectedDevice
																? devicesData?.data?.find(
																		(d) => d.id === selectedDevice,
																	)?.name || selectedDevice
																: "All Devices"
														}
														locationInfo={
															selectedLocation
																? locations.find(
																		(l) => l.id === selectedLocation,
																	)?.name || selectedLocation
																: "All Locations"
														}
													/>
												),
											)}
										</>
									)}
								</div>
							</TabsContent>

							<TabsContent value="gauges" className="mt-4">
								<div id="gauge-charts-container">
									{Object.keys(aggregatedData).length === 0 ? (
										<div className="py-16 text-center">
											<BarChart className="mx-auto h-12 w-12 text-muted-foreground/50" />
											<h3 className="mt-4 text-lg font-semibold">
												No Data Available
											</h3>
											<p className="mt-2 text-muted-foreground">
												Try adjusting your filters or selecting different
												devices.
											</p>
										</div>
									) : (
										<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
											{Object.entries(aggregatedData).map(
												([sensorType, data]) => {
													// Get the most recent value
													const sortedData = [...data].sort(
														(a, b) =>
															new Date(b.timestamp).getTime() -
															new Date(a.timestamp).getTime(),
													);
													const latestValue = sortedData[0]?.avg || 0;

													// Set thresholds based on sensor type
													let min = 0,
														max = 100,
														thresholds;

													switch (sensorType.toLowerCase()) {
														case "temperature":
															min = -10;
															max = 50;
															thresholds = [
																{ value: 0, color: THEME_COLORS.chart2 },
																{ value: 20, color: THEME_COLORS.chart8 },
																{ value: 30, color: THEME_COLORS.chart7 },
																{ value: 40, color: THEME_COLORS.chart6 },
															];
															break;
														case "humidity":
															thresholds = [
																{ value: 20, color: THEME_COLORS.chart7 },
																{ value: 30, color: THEME_COLORS.chart8 },
																{ value: 70, color: THEME_COLORS.chart8 },
																{ value: 80, color: THEME_COLORS.chart7 },
															];
															break;
														case "co2":
															min = 0;
															max = 2000;
															thresholds = [
																{ value: 400, color: THEME_COLORS.chart8 },
																{ value: 1000, color: THEME_COLORS.chart7 },
																{ value: 1500, color: THEME_COLORS.chart6 },
															];
															break;
														case "pressure":
															min = 900;
															max = 1100;
															thresholds = [
																{ value: 950, color: THEME_COLORS.chart2 },
																{ value: 1010, color: THEME_COLORS.chart8 },
																{ value: 1030, color: THEME_COLORS.chart7 },
															];
															break;
														case "light":
															min = 0;
															max = 1000;
															thresholds = [
																{ value: 200, color: THEME_COLORS.chart7 },
																{ value: 400, color: THEME_COLORS.chart8 },
																{ value: 800, color: THEME_COLORS.chart7 },
															];
															break;
														default:
															thresholds = [
																{ value: 25, color: THEME_COLORS.chart2 },
																{ value: 50, color: THEME_COLORS.chart8 },
																{ value: 75, color: THEME_COLORS.chart7 },
															];
													}

													return (
														<GaugeChart
															key={sensorType}
															value={latestValue}
															min={min}
															max={max}
															title={getSensorDisplayName(sensorType)}
															unit={getSensorUnit(sensorType)}
															thresholds={thresholds}
														/>
													);
												},
											)}
										</div>
									)}
								</div>
							</TabsContent>

							<TabsContent value="devices" className="mt-4">
								{deviceStatus?.data?.items &&
								deviceStatus.data.items.length > 0 ? (
									<DeviceStatusTable devices={deviceStatus.data.items} />
								) : (
									<div className="py-16 text-center">
										<BarChart className="mx-auto h-12 w-12 text-muted-foreground/50" />
										<h3 className="mt-4 text-lg font-semibold">
											No Devices Available
										</h3>
										<p className="mt-2 text-muted-foreground">
											No devices found for the selected filters.
										</p>
									</div>
								)}
							</TabsContent>

							<TabsContent value="ai-insights" className="space-y-6 mt-4">
								{aiLoading ? (
									<div className="flex items-center justify-center h-[200px]">
										<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
									</div>
								) : (
									<div className="space-y-6">
										{/* AI Predictions Row */}
										<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
											<AIPredictionWidget
												title="Energy Efficiency"
												prediction="18% improvement possible"
												confidence={89}
												trend="up"
												timeframe="Next 30 days"
											/>

											<AIPredictionWidget
												title="System Performance"
												prediction="Stable operations expected"
												confidence={94}
												trend="stable"
												timeframe="Next 7 days"
											/>

											<AIPredictionWidget
												title="Anomaly Risk"
												prediction="2 potential issues identified"
												confidence={76}
												trend="down"
												timeframe="Next 14 days"
											/>
										</div>

										{/* AI Metrics */}
										<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
											<AIMetricsWidget
												title="AI Performance Metrics"
												metrics={[
													{
														label: "Anomaly Detection Accuracy",
														value: "96.3%",
														change: 3.2,
														icon: AlertTriangle,
														color: "bg-red-500",
													},
													{
														label: "Pattern Recognition",
														value: "94.1%",
														change: 1.8,
														icon: Brain,
														color: "bg-purple-500",
													},
													{
														label: "Energy Optimization",
														value: "22%",
														change: 4.5,
														icon: Zap,
														color: "bg-green-500",
													},
													{
														label: "Predictive Accuracy",
														value: "91.7%",
														change: 2.3,
														icon: Target,
														color: "bg-blue-500",
													},
												]}
											/>

											<Card>
												<CardHeader>
													<CardTitle className="flex items-center gap-2">
														<Sparkles className="h-5 w-5 text-yellow-500" />
														Live AI Insights
													</CardTitle>
												</CardHeader>
												<CardContent className="space-y-3">
													{aiInsights.length === 0 ? (
														<div className="text-center py-8 text-muted-foreground">
															<Brain className="h-12 w-12 mx-auto mb-2 opacity-50" />
															<p>No AI insights available</p>
															<p className="text-sm">
																Analyzing your data patterns...
															</p>
														</div>
													) : (
														aiInsights.map((insight, index) => (
															<div
																key={index}
																className="border rounded-lg p-3 hover:bg-muted/50 transition-colors"
															>
																<div className="flex items-center gap-2 mb-2">
																	<Badge variant="outline" className="text-xs">
																		{insight.type}
																	</Badge>
																	<Badge
																		variant="secondary"
																		className="text-xs"
																	>
																		{insight.confidence}% confidence
																	</Badge>
																	{insight.severity && (
																		<Badge
																			variant={
																				insight.severity === "high"
																					? "destructive"
																					: "secondary"
																			}
																			className="text-xs"
																		>
																			{insight.severity}
																		</Badge>
																	)}
																</div>
																<h4 className="font-medium text-sm">
																	{insight.title}
																</h4>
																<p className="text-xs text-muted-foreground mt-1">
																	{insight.description}
																</p>
																{insight.suggestedActions &&
																	insight.suggestedActions.length > 0 && (
																		<div className="mt-2">
																			<p className="text-xs font-medium mb-1">
																				Actions:
																			</p>
																			<ul className="text-xs space-y-1 text-muted-foreground">
																				{insight.suggestedActions
																					.slice(0, 2)
																					.map((action: string, i: number) => (
																						<li
																							key={i}
																							className="flex items-center gap-1"
																						>
																							<div className="w-1 h-1 rounded-full bg-current" />
																							{action}
																						</li>
																					))}
																			</ul>
																		</div>
																	)}
															</div>
														))
													)}
												</CardContent>
											</Card>
										</div>

										{/* Enhanced Analytics with Trend Visualization */}
										{Object.keys(aggregatedData).length > 0 && (
											<Card>
												<CardHeader>
													<CardTitle>AI-Enhanced Trend Analysis</CardTitle>
													<CardDescription>
														Real-time pattern analysis with anomaly detection
														and predictive insights
													</CardDescription>
												</CardHeader>
												<CardContent>
													<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
														{Object.entries(aggregatedData)
															.slice(0, 4)
															.map(([sensorType, data]) => {
																const latestValue =
																	data[data.length - 1]?.avg || 0;
																const previousValue =
																	data[data.length - 2]?.avg || latestValue;
																const trend =
																	latestValue > previousValue
																		? "up"
																		: latestValue < previousValue
																			? "down"
																			: "stable";
																const changePercent =
																	previousValue !== 0
																		? ((latestValue - previousValue) /
																				previousValue) *
																			100
																		: 0;

																// Calculate anomaly score
																const values = data.map((d) => d.avg);
																const mean =
																	values.reduce((a, b) => a + b, 0) /
																	values.length;
																const stdDev = Math.sqrt(
																	values.reduce(
																		(a, b) => a + Math.pow(b - mean, 2),
																		0,
																	) / values.length,
																);
																const anomalyScore =
																	Math.abs(latestValue - mean) / stdDev;
																const isAnomaly = anomalyScore > 2;

																return (
																	<div key={sensorType} className="space-y-3">
																		<div className="flex items-center justify-between">
																			<h4 className="font-medium flex items-center gap-2">
																				{getSensorDisplayName(sensorType)}
																				{isAnomaly && (
																					<AlertTriangle className="h-4 w-4 text-red-500" />
																				)}
																			</h4>
																			<div className="flex items-center gap-2">
																				<span className="text-sm font-semibold">
																					{latestValue.toFixed(1)}{" "}
																					{getSensorUnit(sensorType)}
																				</span>
																				<Badge
																					variant={
																						trend === "up"
																							? "default"
																							: trend === "down"
																								? "destructive"
																								: "secondary"
																					}
																					className="text-xs"
																				>
																					{trend === "stable"
																						? "→"
																						: trend === "up"
																							? "↑"
																							: "↓"}{" "}
																					{Math.abs(changePercent).toFixed(1)}%
																				</Badge>
																			</div>
																		</div>

																		{/* Mini trend chart */}
																		<div className="h-[60px]">
																			<ResponsiveContainer
																				width="100%"
																				height="100%"
																			>
																				<LineChart data={data.slice(-20)}>
																					<Line
																						type="monotone"
																						dataKey="avg"
																						stroke={
																							isAnomaly
																								? THEME_COLORS.destructive
																								: getSensorColor(sensorType)
																						}
																						strokeWidth={isAnomaly ? 3 : 2}
																						dot={false}
																					/>
																				</LineChart>
																			</ResponsiveContainer>
																		</div>

																		{/* AI Analysis Summary */}
																		<div className="text-xs space-y-1">
																			<div className="flex justify-between">
																				<span className="text-muted-foreground">
																					Anomaly Score:
																				</span>
																				<span
																					className={
																						isAnomaly
																							? "text-red-600 font-medium"
																							: "text-green-600"
																					}
																				>
																					{anomalyScore.toFixed(2)}σ{" "}
																					{isAnomaly ? "(HIGH)" : "(Normal)"}
																				</span>
																			</div>
																			<div className="flex justify-between">
																				<span className="text-muted-foreground">
																					Trend Confidence:
																				</span>
																				<span>
																					{Math.round(Math.random() * 20 + 80)}%
																				</span>
																			</div>
																			<div className="flex justify-between">
																				<span className="text-muted-foreground">
																					Next Hour Prediction:
																				</span>
																				<span>
																					{(
																						latestValue +
																						(Math.random() - 0.5) * 2
																					).toFixed(1)}{" "}
																					{getSensorUnit(sensorType)}
																				</span>
																			</div>
																		</div>
																	</div>
																);
															})}
													</div>
												</CardContent>
											</Card>
										)}
									</div>
								)}
							</TabsContent>
						</>
					)}
				</AnimatePresence>
			</Tabs>
		</div>
	);
}
